import { Box } from '@mui/material';
import {
  AutoComplete,
  getValueByDotNotation,
  ReadOnly,
  useDataProvider,
  useEntityPermission,
} from '@ui-framework/index';
import { useEffect, useState, forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';

const formatAddress = (
  { address, city, state, country, zip },
  fieldPermissions,
) => {
  const array = [];
  if (address && fieldPermissions?.address) {
    array.push(address);
  }
  if (city && fieldPermissions?.city) {
    array.push(city);
  }
  if (state && fieldPermissions?.state) {
    array.push(state);
  }
  if (country && fieldPermissions?.country) {
    array.push(country);
  }
  if (zip && fieldPermissions?.zip) {
    array.push(zip);
  }
  if (array?.length > 0) {
    return array.join(', ');
  }
  return null;
};

export const getCurrentAddress = (
  currentValues,
  dependencies,
  dependencyPermissions,
  permissions,
) => {
  const formAddress = {
    address: getValueByDotNotation(currentValues, dependencies?.address),
    city: getValueByDotNotation(currentValues, dependencies?.city),
    state: getValueByDotNotation(currentValues, dependencies?.state),
    country: getValueByDotNotation(currentValues, dependencies?.country),
    zip: getValueByDotNotation(currentValues, dependencies?.zip),
  };

  const fieldPermissions = {
    address: getValueByDotNotation(permissions, dependencyPermissions?.address),
    city: getValueByDotNotation(permissions, dependencyPermissions?.city),
    state: getValueByDotNotation(permissions, dependencyPermissions?.state),
    country: getValueByDotNotation(permissions, dependencyPermissions?.country),
    zip: getValueByDotNotation(permissions, dependencyPermissions?.zip),
  };

  const currentFormattedAddress = formatAddress(formAddress, fieldPermissions);

  const currentAddress = currentFormattedAddress
    ? {
        value: currentFormattedAddress,
        label: currentFormattedAddress || '',
      }
    : null;

  return currentAddress;
};

const AddressValidator = forwardRef(
  ({ overwriteProps, formPermissions = {}, ...props }, ref) => {
    const nameKey = 'address';
    const valueKey = 'id';

    const permissions = formPermissions;

    const provider =
      process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
    const { get, getApiUrl } = useDataProvider(provider);
    const { getValues, setValue, watch } = useFormContext();
    const sessionTokenName = props?.field?.dependencies?.sessionToken;

    const [sessionToken, setSessionToken] = useState(null);
    const [defaultOptions, setDefaultOptions] = useState([]);

    const lastAddressValues = getValues();
    const lastAddress = getCurrentAddress(
      lastAddressValues,
      props?.field?.dependencies,
      props?.field?.dependencyPermissions,
      permissions,
    );

    const currentAddressValues = watch();

    const currentAddress = getCurrentAddress(
      currentAddressValues,
      props?.field?.dependencies,
      props?.field?.dependencyPermissions,
      permissions,
    );

    const createSession = async () => {
      const result = await get(
        `${getApiUrl()}/sxcl/v1/geocoding/generate-session-token`,
      );
      if (result.ok) {
        return result;
      }
      return null;
    };

    const getAddress = async ({ searchValue }) => {
      const result = await get(
        `${getApiUrl()}/sxcl/v1/geocoding/autocomplete-address?partialAddress=${encodeURIComponent(
          searchValue,
        )}&sessionToken=${sessionToken}`,
      );
      if (result.ok) {
        const resultsFound =
          result?.data?.map(item => ({
            label: item[nameKey],
            value: item[valueKey],
          })) || [];
        return resultsFound;
      }
      return null;
    };

    const loadOptions = async (searchValue, callback) => {
      if (!searchValue) return [];
      if (searchValue?.length > 1) {
        const resultsFound = await getAddress({
          searchValue,
        });
        if (resultsFound) {
          return callback(resultsFound);
        }
      }
      return callback([]);
    };

    const initialize = async () => {
      const newSession = await createSession();
      setSessionToken(newSession?.data?.sessionToken);
    };

    const { isHidden, isReadOnly } = useEntityPermission();

    if (isReadOnly(props?.permission, lastAddress?.value)) {
      return (
        <ReadOnly name={props?.name}>
          {lastAddress?.label || lastAddress?.value}
        </ReadOnly>
      );
    }

    if (isHidden(props?.permission)) {
      return (
        <input
          type="hidden"
          name={props?.name}
          value={lastAddress?.value || ''}
        />
      );
    }

    useEffect(() => {
      initialize();
    }, []);

    useEffect(() => {
      if (lastAddress?.value !== currentAddress?.value) {
        initialize();
      }
    }, [currentAddress?.value, lastAddress?.value]);

    useEffect(() => {
      const options = lastAddress?.value ? [lastAddress] : [];
      setDefaultOptions(options);
    }, [lastAddress?.value]);

    return (
      <Box>
        <AutoComplete
          ref={ref}
          defaultOptions={defaultOptions}
          loadOptions={loadOptions}
          {...props}
          overwriteProps={overwriteProps}
          isClearable
          onChangeOther={selectedOption => {
            if (selectedOption?.value !== lastAddress?.value) {
              setValue(sessionTokenName, sessionToken, { shouldDirty: true });
            }
          }}
        />
      </Box>
    );
  },
);

export default AddressValidator;
